import React, { useState, useEffect } from 'react';
import { TrendingUp, Plus, Play, Pause, BarChart3, DollarSign } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import Header from '../components/Header';
import Footer from '../components/Footer';

const CampaignsPage = () => {
  const { user } = useAuth();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [stats, setStats] = useState({
    impressions: 0,
    clicks: 0,
    spent: 0,
    conversions: 0
  });

  // Carregar campanhas reais do Firestore
  useEffect(() => {
    loadCampaigns();
  }, [user]);

  const loadCampaigns = async () => {
    if (!user?.id) {
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/business/${user.id}/campaigns`);
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          const campaignsList = data.campaigns || [];
          setCampaigns(campaignsList);
          
          // Calcular estatísticas totais
          const totalStats = campaignsList.reduce((acc, campaign) => ({
            impressions: acc.impressions + (campaign.impressions || 0),
            clicks: acc.clicks + (campaign.clicks || 0),
            spent: acc.spent + (campaign.spent || 0),
            conversions: acc.conversions + (campaign.conversions || 0)
          }), { impressions: 0, clicks: 0, spent: 0, conversions: 0 });
          
          setStats(totalStats);
        } else {
          console.error('Erro ao carregar campanhas:', data.error);
        }
      } else {
        console.error('Erro na requisição de campanhas:', response.status);
      }
    } catch (error) {
      console.error('Erro ao carregar campanhas:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'paused':
        return 'bg-yellow-100 text-yellow-800';
      case 'completed':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-secondary-100 text-secondary-800';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'active':
        return 'Ativa';
      case 'paused':
        return 'Pausada';
      case 'completed':
        return 'Finalizada';
      default:
        return 'Desconhecido';
    }
  };

  const calculateCTR = (clicks, impressions) => {
    return impressions > 0 ? ((clicks / impressions) * 100).toFixed(2) : '0.00';
  };

  const calculateCPC = (spent, clicks) => {
    return clicks > 0 ? (spent / clicks).toFixed(2) : '0.00';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-secondary-50">
        <Header />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-secondary-50">
      <Header />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-secondary-900 font-display">
              Campanhas de Tráfego
            </h1>
            <p className="text-secondary-600 mt-1">
              Gerencie suas campanhas de marketing digital com IA
            </p>
          </div>
          <button
            onClick={() => setShowModal(true)}
            className="btn-primary flex items-center space-x-2 mt-4 sm:mt-0"
          >
            <Plus className="w-4 h-4" />
            <span>Nova Campanha</span>
          </button>
        </div>

        {/* Resumo Geral */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="card">
            <div className="flex items-center">
              <div className="bg-blue-100 p-3 rounded-lg">
                <BarChart3 className="w-6 h-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-secondary-600">Impressões</p>
                <p className="text-2xl font-bold text-secondary-900">
                  {stats.impressions > 0 ? stats.impressions.toLocaleString() : '0'}
                </p>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="flex items-center">
              <div className="bg-green-100 p-3 rounded-lg">
                <TrendingUp className="w-6 h-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-secondary-600">Cliques</p>
                <p className="text-2xl font-bold text-secondary-900">
                  {stats.clicks || '0'}
                </p>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="flex items-center">
              <div className="bg-purple-100 p-3 rounded-lg">
                <DollarSign className="w-6 h-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-secondary-600">Gasto Total</p>
                <p className="text-2xl font-bold text-secondary-900">
                  R$ {stats.spent ? stats.spent.toFixed(2) : '0,00'}
                </p>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="flex items-center">
              <div className="bg-primary-100 p-3 rounded-lg">
                <TrendingUp className="w-6 h-6 text-primary-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-secondary-600">Conversões</p>
                <p className="text-2xl font-bold text-secondary-900">
                  {stats.conversions || '0'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Lista de Campanhas */}
        {campaigns.length === 0 ? (
          <div className="card text-center py-12">
            <TrendingUp className="w-16 h-16 text-secondary-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-secondary-900 mb-2">
              Nenhuma campanha criada
            </h3>
            <p className="text-secondary-600 mb-6">
              Crie sua primeira campanha e deixe nossa IA otimizar seus resultados
            </p>
            <button
              onClick={() => setShowModal(true)}
              className="btn-primary"
            >
              Criar Primeira Campanha
            </button>
          </div>
        ) : (
          <div className="space-y-6">
            {campaigns.map((campaign) => (
              <div key={campaign.id} className="card-hover">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h3 className="text-lg font-semibold text-secondary-900">
                          {campaign.name}
                        </h3>
                        <p className="text-sm text-secondary-600">
                          {campaign.platform} • {campaign.startDate} até {campaign.endDate}
                        </p>
                      </div>
                      <div className="flex items-center space-x-3">
                        <span className={`text-xs px-2 py-1 rounded-full ${getStatusColor(campaign.status)}`}>
                          {getStatusText(campaign.status)}
                        </span>
                        <button className="text-secondary-400 hover:text-primary-600">
                          {campaign.status === 'active' ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-6 gap-4 text-sm">
                      <div>
                        <p className="text-secondary-600">Orçamento</p>
                        <p className="font-semibold text-secondary-900">R$ {campaign.budget.toFixed(2)}</p>
                      </div>
                      <div>
                        <p className="text-secondary-600">Gasto</p>
                        <p className="font-semibold text-secondary-900">R$ {campaign.spent.toFixed(2)}</p>
                      </div>
                      <div>
                        <p className="text-secondary-600">Impressões</p>
                        <p className="font-semibold text-secondary-900">{campaign.impressions.toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-secondary-600">Cliques</p>
                        <p className="font-semibold text-secondary-900">{campaign.clicks}</p>
                      </div>
                      <div>
                        <p className="text-secondary-600">CTR</p>
                        <p className="font-semibold text-secondary-900">{calculateCTR(campaign.clicks, campaign.impressions)}%</p>
                      </div>
                      <div>
                        <p className="text-secondary-600">CPC</p>
                        <p className="font-semibold text-secondary-900">R$ {calculateCPC(campaign.spent, campaign.clicks)}</p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 lg:mt-0 lg:ml-6">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-green-600">{campaign.conversions}</p>
                      <p className="text-sm text-secondary-600">Conversões</p>
                    </div>
                  </div>
                </div>

                {/* Barra de Progresso do Orçamento */}
                <div className="mt-4">
                  <div className="flex justify-between text-sm text-secondary-600 mb-1">
                    <span>Orçamento utilizado</span>
                    <span>{((campaign.spent / campaign.budget) * 100).toFixed(1)}%</span>
                  </div>
                  <div className="w-full bg-secondary-200 rounded-full h-2">
                    <div 
                      className="bg-primary-500 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${Math.min((campaign.spent / campaign.budget) * 100, 100)}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Modal de Nova Campanha */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-lg w-full p-6">
              <h2 className="text-xl font-bold text-secondary-900 mb-4">
                Nova Campanha
              </h2>
              
              <form className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Nome da Campanha
                  </label>
                  <input
                    type="text"
                    className="input-field"
                    placeholder="Ex: Promoção de Verão"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Plataforma
                  </label>
                  <select className="input-field">
                    <option value="">Selecione a plataforma...</option>
                    <option value="google">Google Ads</option>
                    <option value="meta">Meta Ads (Facebook/Instagram)</option>
                    <option value="both">Ambas as plataformas</option>
                  </select>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-2">
                      Orçamento Diário (R$)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      className="input-field"
                      placeholder="50,00"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-2">
                      Duração (dias)
                    </label>
                    <input
                      type="number"
                      className="input-field"
                      placeholder="14"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Objetivo da Campanha
                  </label>
                  <select className="input-field">
                    <option value="">Selecione o objetivo...</option>
                    <option value="traffic">Aumentar Tráfego</option>
                    <option value="conversions">Gerar Conversões</option>
                    <option value="awareness">Aumentar Reconhecimento</option>
                    <option value="leads">Capturar Leads</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Público-Alvo
                  </label>
                  <textarea
                    className="input-field"
                    rows="3"
                    placeholder="Descreva seu público-alvo (idade, interesses, localização, etc.)"
                  />
                </div>
              </form>
              
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowModal(false)}
                  className="btn-secondary"
                >
                  Cancelar
                </button>
                <button className="btn-primary">
                  Criar Campanha
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default CampaignsPage;

